import { ActivityMenuItem, CustomTestData } from "Hub/pages/TaskDetails/types";
import { TaskStatusEnum } from "Hub/pages/Tasks/types";
import { createSelector } from "reselect";
import { Methodology } from "types/Methodology";
import { SubmissionSlotExpectedFormatEnum } from "types/SubmissionSlots";

import {
  ClassificationMapped,
  ParametersMappedData,
  TaskActivity,
  TaskDetails,
  TaskMethodology,
  VarianceChecksList,
} from "API/Endpoints/Tasks/payloads";
import { apiStrings } from "API/apiStrings";

import { RootState } from "store/store";
import { StateFn } from "store/types";

import { TaskDetailsState } from "./taskDetailsStore";
import {
  CalculationTestData,
  ErrorProps,
  FilesUploadData,
  MissingEntity,
  PristineInputDataFiles,
  ReloadTrigger,
  Tab,
  TabVisibility,
} from "./types";

const getStore = (state: RootState): TaskDetailsState => state.hub.taskDetails;

// Selectors

const currentTaskDetailsData: StateFn<TaskDetails | null> = (
  state: RootState
) => getStore(state).currentTaskDetailsData;

const selectedMenuItemId: StateFn<string> = (state: RootState) =>
  getStore(state).selectedMenuItemId;

const menuItems: StateFn<ActivityMenuItem[]> = (state: RootState) =>
  getStore(state).menuItems;

const missingDataMethodologyTab: StateFn<Tab> = (state: RootState) =>
  getStore(state).missingDataMethodologyTab;

const isTaskClaimedByCurrentUser: StateFn<boolean> = (state: RootState) =>
  getStore(state).isTaskClaimedByCurrentUser;

const filesUploadData: StateFn<FilesUploadData | null> = (state: RootState) =>
  getStore(state).filesUploadData;

const parametersMappedData: StateFn<ParametersMappedData | null> = (
  state: RootState
) => getStore(state).parametersMappedData;

const classifiedData: StateFn<ClassificationMapped | null> = (
  state: RootState
) => getStore(state).classifiedData;

const varianceChecksData: StateFn<VarianceChecksList | null> = (
  state: RootState
) => getStore(state).varianceChecksData;

const submitEnabled: StateFn<boolean> = (state: RootState) =>
  getStore(state).submitEnabled;

const errorData: StateFn<ErrorProps | null> = (state: RootState) =>
  getStore(state).errorData;

const tabVisibility: StateFn<TabVisibility> = (state: RootState) =>
  getStore(state).tabVisibility;

const methodologies: StateFn<Methodology[]> = (state: RootState) =>
  getStore(state).methodologies;

const isMenuFocus: StateFn<boolean> = (state: RootState) =>
  getStore(state).isMenuFocus;

const encodedCalculationTestData: StateFn<CalculationTestData | null> = (
  state: RootState
) => getStore(state).encodedCalculationTestData;

const customFileData: StateFn<CustomTestData | null> = (state: RootState) =>
  getStore(state).customFileData;

const reportedEmissionsData: StateFn<CalculationTestData | null> = (
  state: RootState
) => getStore(state).reportedEmissionsData;

const missingEntities: StateFn<MissingEntity[]> = (state: RootState) =>
  getStore(state).missingEntities;

const busyMessages: StateFn<string[]> = (state: RootState) =>
  getStore(state).busyMessages;

const reloadTrigger: StateFn<ReloadTrigger | undefined> = (state: RootState) =>
  getStore(state).reloadTrigger;

// Selectors with derived values (need createSelector)

const missingEntitiesForActivityMethodology: StateFn<MissingEntity[]> =
  createSelector(
    missingEntities,
    selectedMenuItemId,
    (missingEntities, selectedMenuItemId) => {
      return missingEntities?.filter(
        (entity) => entity.menuId === selectedMenuItemId
      );
    }
  );

const pristineInputDataFiles: StateFn<PristineInputDataFiles> = createSelector(
  currentTaskDetailsData,
  (currentTaskDetailsData) =>
    currentTaskDetailsData?.activityMethodologies?.flatMap(
      (activityMethodology) =>
        (activityMethodology.methodologies ?? [])
          .filter(
            (item) =>
              item.inputDataFile !== null && item.inputDataFile !== undefined
          )
          .map((item) => ({
            id: `${activityMethodology.activityId}|${item.id}`,
            inputDataFile: item.inputDataFile,
          }))
    )
);

const isIntegration: StateFn<boolean> = createSelector(
  currentTaskDetailsData,
  (currentTaskDetailsData) =>
    currentTaskDetailsData?.activityMethodologies.some(
      (item) =>
        item.expectedDataFormat === SubmissionSlotExpectedFormatEnum.INTEGRATION
    ) || false
);

const taskActivities: StateFn<TaskActivity[] | undefined> = createSelector(
  currentTaskDetailsData,
  (currentTaskDetailsData) =>
    (
      currentTaskDetailsData?.activityMethodologies.map((activity) => ({
        ...activity,
        methodologies: (activity.methodologies ?? [])
          .slice()
          .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? "")),
      })) || []
    ).sort((a, b) => a.activityName.localeCompare(b.activityName))
);

const selectedActivityId: StateFn<string | undefined> = createSelector(
  selectedMenuItemId,
  (selectedMenuItemId) => {
    const [selectedActivityId] = selectedMenuItemId.split("|");
    return selectedActivityId;
  }
);

const selectedMethodologyId: StateFn<string | undefined> = createSelector(
  selectedMenuItemId,
  (selectedMenuItemId) => {
    const [, selectedMethodologyId] = selectedMenuItemId.split("|");
    return selectedMethodologyId;
  }
);

const selectedActivity: StateFn<TaskActivity | undefined> = createSelector(
  taskActivities,
  selectedActivityId,
  (taskActivities, selectedActivityId) => {
    if (!taskActivities || !selectedActivityId) {
      return undefined;
    }
    return taskActivities?.find(
      (activity) => activity.activityId === selectedActivityId
    );
  }
);

const selectedMethodology: StateFn<TaskMethodology | undefined> =
  createSelector(
    selectedActivity,
    selectedMethodologyId,
    (selectedActivity, selectedMethodologyId) => {
      if (!selectedActivity || !selectedMethodologyId) {
        return undefined;
      }

      return selectedActivity.methodologies.find(
        (methodology) => methodology.id === selectedMethodologyId
      );
    }
  );

const menuItemErrorIndicators: StateFn<string[]> = createSelector(
  taskActivities,
  missingEntities,
  (taskActivities, missingEntities) => {
    if (!taskActivities) {
      return [];
    }

    const menuIds = new Set<string>();

    // add items where errorLog is present
    taskActivities
      .flatMap((activity) =>
        activity.methodologies
          .filter((methodology) => methodology.errorLog)
          .map((methodology) => `${activity.activityId}|${methodology.id}`)
      )
      .forEach((id) => menuIds.add(id));

    // add items where missingEntities are present
    (missingEntities ?? [])
      .filter(
        (entity) =>
          ![apiStrings.NOT_APPLICABLE, apiStrings.NOT_YET_AVAILABLE].includes(
            entity.status
          )
      )
      .forEach((entity) => {
        menuIds.add(entity.menuId);
      });

    return Array.from(menuIds);
  }
);

const taskStatus: StateFn<string | undefined> = createSelector(
  currentTaskDetailsData,
  (currentTaskDetailsData) => currentTaskDetailsData?.status.value
);

const canGetSubmittedFiles: StateFn<boolean> = createSelector(
  currentTaskDetailsData,
  (currentTaskDetailsData) =>
    Boolean(currentTaskDetailsData?.status.value) &&
    currentTaskDetailsData?.status.value !==
      TaskStatusEnum.PENDING_CLASSIFICATION
);

const isBackToVarianceCleaning: StateFn<boolean> = createSelector(
  currentTaskDetailsData,
  (currentTaskDetailsData) =>
    currentTaskDetailsData?.status.value === TaskStatusEnum.PENDING_CLEANING &&
    (currentTaskDetailsData?.previousState ===
      "escalated_variance_checks_to_submitter" ||
      currentTaskDetailsData?.previousState ===
        "escalated_variance_checks_to_manager")
);

export const taskDetailsSelectors = {
  busyMessages,
  canGetSubmittedFiles,
  classifiedData,
  currentTaskDetailsData,
  customFileData,
  encodedCalculationTestData,
  errorData,
  filesUploadData,
  isBackToVarianceCleaning,
  isIntegration,
  isMenuFocus,
  isTaskClaimedByCurrentUser,
  menuItemErrorIndicators,
  menuItems,
  methodologies,
  missingDataMethodologyTab,
  missingEntities,
  missingEntitiesForActivityMethodology,
  parametersMappedData,
  pristineInputDataFiles,
  reloadTrigger,
  reportedEmissionsData,
  selectedActivity,
  selectedActivityId,
  selectedMenuItemId,
  selectedMethodology,
  selectedMethodologyId,
  submitEnabled,
  tabVisibility,
  taskActivities,
  taskStatus,
  varianceChecksData,
};
